/**
 * Lives inside each tab's DetachedRouter. Reports the tab's inner location to
 * the shell (URL sync, spec §4) and tracks the inner history depth that drives
 * `canGoBack`. Depth is counted, never inferred from `location.key`.
 */
import { useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { isShellPath } from './shellRoutes';
import { ShellContext } from './shellContext';
import { TabDepthContext, useTab } from './TabContext';

export function TabLocationSync(): null {
  const { tabId } = useTab();
  const shell = useContext(ShellContext);
  const setDepth = useContext(TabDepthContext);
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const register = shell?.registerTabNavigator;
  useEffect(
    () => register?.(tabId, (path, opts) => navigateRef.current(path, opts)),
    [register, tabId],
  );

  const onTabLocation = shell?.onTabLocation;
  const depth = useRef(0);
  const lastKey = useRef(location.key); // the initial entry is not a navigation
  useEffect(() => {
    if (location.key === lastKey.current) return;
    lastKey.current = location.key;
    const path = location.pathname + location.search;
    // An escape PUSH (non-shell path) is reverted by the shell; it is not history depth.
    if (navType === 'PUSH' && isShellPath(path)) depth.current += 1;
    else if (navType === 'POP') depth.current = Math.max(0, depth.current - 1);
    setDepth(depth.current);
    onTabLocation?.(tabId, path, navType);
  }, [location, navType, tabId, setDepth, onTabLocation]);

  return null;
}

export default TabLocationSync;
